// components/Dashboard/UsersManagement.styles.js
import styled from "styled-components";

export const Container = styled.div`
  padding: 20px;
  margin: 0 auto;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e1e1e1;
`;

export const Title = styled.h1`
  color: #333;
  font-size: 1.8rem;
  margin: 0;
`;

export const RefreshButton = styled.button`
  background: linear-gradient(90deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #5a67d8;
    transform: translateY(-2px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

export const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

export const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #e1e1e1;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;

export const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #667eea;
  margin-bottom: 5px;
`;

export const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  overflow: scroll;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #e1e1e1;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHead = styled.thead`
  background: #f7fafc;
`;

export const TableHeader = styled.th`
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #e1e1e1;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
`;

export const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f9f9f9;
  }

  &:hover {
    background: #f0f4f8;
  }
`;

export const TableCell = styled.td`
  padding: 15px;
  border-bottom: 1px solid #e1e1e1;
  color: #333;
  font-size: 0.9rem;
`;

export const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ status }) => {
    switch (status) {
      case "approved":
        return "background: #c6f6d5; color: #22543d;";
      case "pending":
        return "background: #fed7d7; color: #742a2a;";
      case "admin":
        return "background: #bee3f8; color: #2a4365;";
      default:
        return "background: #e1e1e1; color: #666;";
    }
  }}
`;

export const RoleBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ role }) => {
    switch (role) {
      case "admin":
        return "background: linear-gradient(90deg, #667eea, #764ba2); color: white;";
      case "user":
        return "background: #edf2f7; color: #4a5568;";
      default:
        return "background: #e1e1e1; color: #666;";
    }
  }}
`;

export const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

export const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 5px;

  ${({ variant }) => {
    switch (variant) {
      case "approve":
        return "background: #48bb78; color: white;";
      case "disapprove":
        return "background: #f56565; color: white;";
      case "delete":
        return "background: #c53030; color: white;";
      default:
        return "background: #e1e1e1; color: #333;";
    }
  }}

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: #667eea;
  font-size: 1.2rem;
`;

export const ErrorMessage = styled.div`
  padding: 20px;
  background: #fed7d7;
  color: #742a2a;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SuccessMessage = styled.div`
  padding: 20px;
  background: #c6f6d5;
  color: #22543d;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

export const DateText = styled.div`
  font-size: 0.8rem;
  color: #888;
`;

export const EmailText = styled.div`
  color: #667eea;
  font-weight: 500;
`;

export const UsernameText = styled.div`
  font-weight: 600;
  color: #333;
`;

export const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

export const FilterButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #e1e1e1;
  background: ${({ active }) =>
    active ? "linear-gradient(90deg, #667eea, #764ba2)" : "#f7fafc"};
  color: ${({ active }) => (active ? "white" : "#333")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
  background: ${({ active }) =>
    active ? "linear-gradient(90deg, #667eea, #764ba2)" : "#f7fafc"};
`;
